import { unzipSync, strFromU8 } from "fflate";
import type { PageLines } from "./localParser";

/**
 * Word (.docx) raporundan metin satırları: paragraflar tek satır, tablo satırları hücreler sekmeyle ayrılmış tek satır
 * (PDF'teki tablo satırı gibi) → aynı kural tabanlı okuyucu. Sayfa numarası Word'ün kaydettiği sayfa sonlarından
 * (w:lastRenderedPageBreak, w:br type=page) yaklaşık bulunur. Dosya tarayıcıdan çıkmaz.
 */
export function readDocxText(data: ArrayBuffer): { pages: PageLines[]; pageCount: number; chars: number } {
  let files: Record<string, Uint8Array>;
  try { files = unzipSync(new Uint8Array(data), { filter: (f) => f.name === "word/document.xml" }); } catch { throw new Error("Word dosyası açılamadı (bozuk ya da .doc biçiminde olabilir)"); }
  const xml = files["word/document.xml"];
  if (!xml) throw new Error("Word dosyasında belge gövdesi yok");
  const doc = new DOMParser().parseFromString(strFromU8(xml), "application/xml");
  const W = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
  const body = doc.getElementsByTagNameNS(W, "body")[0];
  const pages: PageLines[] = [{ page: 1, lines: [] }];
  let chars = 0;
  const cur = (): string[] => pages[pages.length - 1]!.lines;
  const breaks = (el: Element): number => {
    let n = el.getElementsByTagNameNS(W, "lastRenderedPageBreak").length;
    for (const br of Array.from(el.getElementsByTagNameNS(W, "br"))) if (br.getAttributeNS(W, "type") === "page" || br.getAttribute("w:type") === "page") n++;
    return n;
  };
  const newPages = (n: number): void => { for (let i = 0; i < n; i++) pages.push({ page: pages.length + 1, lines: [] }); };
  // Paragraf metni: w:t, sekme ve satır sonu; alan kodları (w:instrText: TOC, PAGEREF, SEQ) atlanır
  const paraText = (p: Element): string => {
    let s = "";
    const walk = (n: Element): void => {
      for (const c of Array.from(n.children)) {
        if (c.namespaceURI !== W) { walk(c); continue; }
        if (c.localName === "t") s += c.textContent ?? "";
        else if (c.localName === "tab") s += "\t";
        else if (c.localName === "br" && (c.getAttributeNS(W, "type") ?? c.getAttribute("w:type")) !== "page") s += " ";
        else if (c.localName !== "instrText" && c.localName !== "delText") walk(c);
      }
    };
    walk(p);
    return s.replace(/[ \u00a0]+/g, " ").trim();
  };
  const push = (line: string): void => { if (line) { cur().push(line); chars += line.length; } };
  for (const el of Array.from(body?.children ?? [])) {
    if (el.namespaceURI !== W) continue;
    if (el.localName === "p") { newPages(breaks(el)); push(paraText(el)); }
    else if (el.localName === "tbl") {
      newPages(breaks(el));
      for (const tr of Array.from(el.getElementsByTagNameNS(W, "tr"))) {
        const cells = Array.from(tr.children).filter((c) => c.localName === "tc").map((tc) => Array.from(tc.getElementsByTagNameNS(W, "p")).map(paraText).filter(Boolean).join(" "));
        // Boş hücreler de korunur (sütun sırası başlıkla hizalı kalsın); tümü boş satır atlanır
        if (cells.some(Boolean)) push(cells.join("\t"));
      }
    }
  }
  return { pages: pages.filter((p) => p.lines.length), pageCount: pages.length, chars };
}
